import {
  getEnvironmentUrl,
  type EnvironmentName,
} from '@/constants/environment'

// PropelAuth's frontend origin comes from the active @sweetauth provider, NOT
// process.env: referencing NEXT_PUBLIC_PROPEL_AUTH_* here would inline the
// identifier into every bundle, and knox's static content scan forbids it.
export { authFrontendUrl as AUTH_URL } from '@sweetauth/core'
export const TIPTAP_CLOUD_APP_ID =
  process.env.NEXT_PUBLIC_TIPTAP_CLOUD_APP_ID ?? ''
export const GOOGLE_PICKER_API_KEY =
  process.env.NEXT_PUBLIC_GOOGLE_PICKER_API_KEY ?? ''
export const GOOGLE_PICKER_CLIENT_ID =
  process.env.NEXT_PUBLIC_GOOGLE_PICKER_CLIENT_ID ?? ''
export const GOOGLE_PROJECT_NUMBER =
  process.env.NEXT_PUBLIC_GOOGLE_PROJECT_NUMBER ?? ''
export const AZURE_SWEETSPOT_APP_CLIENT_ID =
  process.env.NEXT_PUBLIC_AZURE_SWEETSPOT_APP_CLIENT_ID ?? ''

const VALID_RUNTIME_ENVIRONMENTS = [
  'local',
  'dev',
  'prod',
  'knox-prod',
] as const
type RuntimeEnvironment = (typeof VALID_RUNTIME_ENVIRONMENTS)[number]

const normalizeRuntimeEnvironment = (
  value: string | undefined,
  envName: string,
): RuntimeEnvironment | '' => {
  if (!value) return ''
  if (VALID_RUNTIME_ENVIRONMENTS.includes(value as RuntimeEnvironment)) {
    return value as RuntimeEnvironment
  }

  throw new Error(
    `${envName} must be one of local, dev, prod, or knox-prod. Received "${value}". Use NEXT_PUBLIC_API_URL/NEXT_PUBLIC_FRONTEND_URL for other deployment-specific hosts.`,
  )
}

const LOCAL_DEV_API_ENVIRONMENT_VALUE = normalizeRuntimeEnvironment(
  process.env.NEXT_PUBLIC_LOCAL_DEV_API_ENVIRONMENT,
  'NEXT_PUBLIC_LOCAL_DEV_API_ENVIRONMENT',
)

// Runtime environment. knox-prod (FedRAMP) is first-class, with its own in-boundary hosts;
// for other deployment-specific hosts use the NEXT_PUBLIC_API_URL/FRONTEND_URL overrides below.
export const ENVIRONMENT = LOCAL_DEV_API_ENVIRONMENT_VALUE
  ? 'local'
  : normalizeRuntimeEnvironment(
      process.env.NEXT_PUBLIC_ENVIRONMENT,
      'NEXT_PUBLIC_ENVIRONMENT',
    )

export const IS_FEDRAMP_ENVIRONMENT =
  process.env.NEXT_PUBLIC_FEDRAMP === 'true' || ENVIRONMENT === 'knox-prod'
export const IS_PROD_LIKE_ENVIRONMENT =
  ENVIRONMENT === 'prod' || ENVIRONMENT === 'knox-prod'

export const INTERCOM_APP_ID = IS_FEDRAMP_ENVIRONMENT
  ? ''
  : (process.env.NEXT_PUBLIC_INTERCOM_APP_ID ?? '')
export const KNOCK_PUBLIC_KEY = IS_FEDRAMP_ENVIRONMENT
  ? ''
  : (process.env.NEXT_PUBLIC_KNOCK_PUBLIC_KEY ?? '')
export const KNOCK_IN_APP_CHANNEL_ID = IS_FEDRAMP_ENVIRONMENT
  ? ''
  : (process.env.NEXT_PUBLIC_KNOCK_IN_APP_CHANNEL_ID ?? '')
export const STRIPE_CUSTOMER_PORTAL_URL = IS_FEDRAMP_ENVIRONMENT
  ? ''
  : (process.env.NEXT_PUBLIC_STRIPE_CUSTOMER_PORTAL_URL ?? '')

export const LOCAL_DEV_API_ENVIRONMENT =
  LOCAL_DEV_API_ENVIRONMENT_VALUE || undefined

export const IS_GREEN_API_ENVIRONMENT =
  process.env.NEXT_PUBLIC_USE_GREEN_API === 'true'

export const NEXT_PUBLIC_UNDER_MAINTENANCE =
  process.env.NEXT_PUBLIC_UNDER_MAINTENANCE === 'true'

export const NEXT_PUBLIC_UNDER_MAINTENANCE_END_TIME =
  process.env.NEXT_PUBLIC_UNDER_MAINTENANCE_END_TIME

export const MCP_ENABLED = process.env.NEXT_PUBLIC_MCP_ENABLED !== 'false'

export const IS_STATIC_EXPORT = process.env.NEXT_PUBLIC_STATIC_EXPORT === 'true'

/**
 * MCP servers visible in the Integrations UI, keyed by `server_name`.
 * Servers not in this set are hidden from users even if they exist in the DB.
 * Set to `null` to show all servers (useful for development).
 */
export const VISIBLE_MCP_SERVER_NAMES: Set<string> | null = new Set([
  'microsoft',
  'salesforce',
])

// Multi-instance support: branch name for tab prefix, dynamic ports
export const BRANCH_NAME = process.env.NEXT_PUBLIC_BRANCH_NAME ?? ''
const API_PORT = process.env.NEXT_PUBLIC_API_PORT ?? '8000'
const FRONTEND_PORT = process.env.NEXT_PUBLIC_FRONTEND_PORT ?? '3000'

// DO NOT MODIFY
// if you need to point to a different API server, you can set the LOCAL_DEV_API_ENVIRONMENT environment variable
// or run `bun run dev-local` to point to the local API server

const apiURL = {
  local: `http://localhost:${API_PORT}`,
  dev: IS_GREEN_API_ENVIRONMENT
    ? 'https://api-green.dev.sweetspotgov.com'
    : 'https://api.dev.sweetspotgov.com',
  prod: IS_GREEN_API_ENVIRONMENT
    ? 'https://api-green.sweetspotgov.com'
    : 'https://api.sweetspotgov.com',
  // FedRAMP boundary: no green/blue host variant.
  'knox-prod': 'https://api.fedramp.sweetspotgov.com',
} satisfies Record<EnvironmentName, string>

const frontendURL = {
  local: `http://localhost:${FRONTEND_PORT}`,
  dev: 'https://console.dev.sweetspotgov.com',
  prod: 'https://console.sweetspotgov.com',
  'knox-prod': 'https://console.fedramp.sweetspotgov.com',
} satisfies Record<EnvironmentName, string>

const commercialFrontendURL = {
  local: `http://localhost:${FRONTEND_PORT}`,
  dev: 'https://console.dev.sweetspotgov.com',
  prod: 'https://console.sweetspotgov.com',
  'knox-prod': 'https://console.sweetspotgov.com',
} satisfies Record<EnvironmentName, string>

const tiptapURL = {
  local: 'https://tiptap.dev.sweetspotgov.com',
  dev: 'https://tiptap.dev.sweetspotgov.com',
  prod: 'https://tiptap.sweetspotgov.com',
  // Fail closed: stay in-boundary, never fall back to the dev tiptap host.
  'knox-prod': 'https://tiptap.fedramp.sweetspotgov.com',
} satisfies Record<EnvironmentName, string>

const apiEnv = LOCAL_DEV_API_ENVIRONMENT ?? ENVIRONMENT
const API_URL_OVERRIDE = process.env.NEXT_PUBLIC_API_URL
const FRONTEND_URL_OVERRIDE =
  process.env.NEXT_PUBLIC_STATIC_FRONTEND_URL ??
  process.env.NEXT_PUBLIC_FRONTEND_URL
const TIPTAP_URL_OVERRIDE = process.env.NEXT_PUBLIC_TIPTAP_URL

export const API_URL = API_URL_OVERRIDE ?? getEnvironmentUrl(apiURL, apiEnv)
export const WS_URL = API_URL.replace('http', 'ws')
export const FRONTEND_URL =
  FRONTEND_URL_OVERRIDE ?? getEnvironmentUrl(frontendURL, ENVIRONMENT)
export const COMMERCIAL_FRONTEND_URL = getEnvironmentUrl(
  commercialFrontendURL,
  ENVIRONMENT,
)
export const TIPTAP_URL =
  TIPTAP_URL_OVERRIDE ?? getEnvironmentUrl(tiptapURL, apiEnv)

// External links
export const SLED_PORTAL_REQUEST_URL =
  process.env.NEXT_PUBLIC_SLED_PORTAL_REQUEST_URL ??
  'https://form.fillout.com/t/1h9nGMHY9Wus'
